<template>
    <v-app-bar v-if="showToolbar" app fixed :color="color" :height="height" class="kimsa-elevation">
        <template v-if="titleToolbar">
            <v-icon v-if="titleToolbar.icon" size="25" :color="colorIcon" left>{{ titleToolbar.icon }}</v-icon>
            <div :class="['toolbar-title text-truncate', { 'white--text': dark }]">
                {{ titleToolbar.name }}
            </div>

            <template v-if="titleToolbar.component">
                <v-spacer />
                <component :is="titleToolbar.component" />
            </template>
        </template>
    </v-app-bar>
</template>

<script>

import ConfigStudent from '@/components/common/toolbar/ConfigStudent'
import ConfigCoach from '@/components/common/toolbar/ConfigCoach'
import ConfigCompany from '@/components/common/toolbar/ConfigCompany'
import FilterCoach from '@/components/common/toolbar/FilterCoach'
import ShowStars from '@/components/common/toolbar/ShowStars'
import Calendar from '@/components/common/toolbar/Calendar'
import ChatStudent from '@/components/common/toolbar/ChatStudent'
import StudentRoutineChat from '@/components/common/toolbar/StudentRoutineChat'
import AddRoutine from '@/components/common/toolbar/AddRoutine'
import AddLibrary from '@/components/common/toolbar/AddLibrary'
import AddExercise from '@/components/common/toolbar/AddExercise'
import ManageEvaluations from '@/components/common/toolbar/ManageEvaluations';
import { StatusBar } from '@/utils/Capacitor';

import { mapMutations, mapState } from "vuex";

export default {
    name: "NewToolbar",
    components: {
        ConfigStudent,
        ConfigCoach,
        ConfigCompany,
        FilterCoach,
        ShowStars,
        Calendar,
        ChatStudent,
        StudentRoutineChat,
        AddRoutine,
        AddLibrary,
        AddExercise,
        ManageEvaluations,
    },
    props: {
        showToolbar: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState(['showCalendar', 'currentRoutine', 'title']),
        color(vm = this) {
            return vm.dark ? '#333333' : '#FFFFFF'
        },
        colorIcon(vm = this) {
            return vm.dark ? '#FFFFFF' : '#03342C'
        },
        height(vm = this) {
            return vm.isIphone ? '92' : '64'
        },
        routeName(vm = this) {
            return vm.$route.name
        },
        dark(vm = this) {
            let routes = [
                'splash',
                'coach.calendar.assignedRoutine',
                'student.coach-profile',
                'student.coach-plans',
                'student.routine.detail',
                'student.routine.detail.exercise',
                'student.routine.competence.detail',
                'student.invitations'
            ]
            return routes.includes(vm.routeName)
        },
        routineName(vm = this) {
            if (vm.title) return vm.$t(vm.title)
            return vm.currentRoutine?.name || ''
        },
        routesInfo(vm = this) {
            return {
                // COACHES
                // COACH HOME
                'coach.home': { name: vm.$t('titles.home'), icon: '$home' },
                // COACH STUDENTS
                'coach.students': { name: vm.$t('titles.coach.students'), icon: '$account_multiple', component: 'ChatStudent' },
                'coach.students.chats': { name: vm.$t('titles.coach.chats'), icon: '$account_multiple' },
                'coach.students.groups': { name: vm.$t('titles.coach.groups'), icon: '$account_multiple' },
                'coach.students.profile': { name: vm.$t('titles.coach.student'), icon: '$account' },
                // COACH LIBRARY
                'coach.library': { name: vm.$t('titles.coach.library'), icon: '$training', component: 'AddLibrary' },
                // COACH BLOCKS
                'coach.blocks.edit': { name: vm.$t('titles.coach.block.edit'), icon: '$training' },
                // COACH ROUTINES
                'coach.routines': { name: vm.$t('titles.coach.routines'), icon: '$training', component: 'AddRoutine' },
                'coach.routines.detail': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                'coach.routines.edit': { name: vm.$t('titles.coach.routine.edit'), icon: '$training' },
                'coach.routines.edit.assigned': { name: vm.$t('titles.coach.routine.edit_assigned'), icon: '$training' },
                'coach.routines.detail.assign': { name: vm.$t('titles.coach.routine.assign'), icon: '$training' },
                'coach.routines.create': { name: vm.$t('titles.coach.routine.create'), icon: '$training' },
                // COACH PROGRAMS
                'coach.routines.program.detail': { name: vm.$t('titles.coach.programs.detail'), icon: '$training' },
                'coach.routines.program.detail.routine': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                'coach.routines.program.routine.edit': { name: vm.$t('titles.coach.programs.edit_routine'), icon: '$training' },
                'coach.routines.program.assign': { name: vm.$t('titles.coach.programs.assign'), icon: '$training' },
                // COACH EXERCISES
                'coach.exercises': { name: vm.$t('titles.coach.exercises'), icon: 'mdi-dumbbell' },
                // COACH CALENDAR
                'coach.calendar': { name: vm.$t('titles.coach.calendar'), icon: '$calendar', component: 'Calendar' },
                'coach.calendar.assignedRoutine': { name: vm.routineName, icon: '$calendar' },
                // COACH STATISTICS
                'coach.statistics': { name: vm.$t('titles.coach.statistics'), icon: '$statistics' },
                // COACH STATISTICS
                'coach.evaluations': { name: vm.$t('titles.coach.evaluations'), icon: 'mdi-book-open-variant', component: 'ManageEvaluations' },
                'coach.evaluations.list': { name: vm.$t('titles.coach.evaluation_templates'), icon: 'mdi-book-open-variant', component: 'ManageEvaluations' },
                'coach.evaluations.statistics': { name: vm.$t('titles.coach.evaluation_statistics'), icon: 'mdi-book-open-variant' },
                // PROFILE
                'coach.profile': { name: vm.$t('titles.coach.profile'), icon: '$account_circle', component: 'ConfigCoach' },
                'coach.profile.edit': { name: vm.$t('titles.coach.profile_edit'), icon: '$account_circle' },
                'coach.payment.edit': { name: vm.$t('titles.coach.payment_method_edit'), icon: '$account_circle' },
                // STUDENTS
                // STUDENT HOME
                'student.home': { name: vm.$t('titles.home'), icon: '$home' },
                // STUDENT COACHES
                'student.coaches': { name: vm.$t('titles.student.coaches'), icon: '$account_multiple', component: 'FilterCoach' },
                'student.coach-profile': { name: vm.$t('titles.student.coach'), icon: '$account_multiple', component: 'ShowStars' },
                'student.coach-plans': { name: vm.$t('titles.student.hire_coach'), icon: '$training', component: 'ShowStars' },
                // STUDENT CHATS
                'student.chats': { name: vm.$t('titles.student.chats'), icon: '$account_multiple' },
                // STUDENT ROUTINES
                'student.routines': { name: vm.$t('titles.student.routines'), icon: '$training', component: 'Calendar' },
                'student.routine.detail': { name: vm.routineName, icon: '$training' },
                'student.routine.detail.exercise': { name: vm.routineName, icon: '$training' },
                'student.routine.competence.detail': { name: vm.routineName, icon: '$training' },
                // STUDENT STATISTICS
                'student.statistics': { name: vm.$t('titles.student.statistics'), icon: '$statistics' },
                // STUDENT PROFILE
                'student.profile': { name: vm.$t('titles.student.profile'), icon: '$account_circle', component: 'ConfigStudent' },
                'student.profile.edit': { name: vm.$t('titles.student.profile_edit'), icon: '$account_circle' },
                // STUDENT PAYMENTS
                'student.payment.edit': { name: vm.$t('titles.student.payment_method_edit'), icon: '$account_circle' },
                // STUDENT INVITATIONS
                'student.invitations': { name: vm.$t('titles.student.invitations'), icon: '$account_circle' },
                // COMPANY
                // COMPANY STUDENTS
                'company.students': { name: vm.$t('titles.company.students'), icon: '$account_multiple' },
                // COMPANY ADMIN
                'company.admin': { name: vm.$t('titles.company.admin'), icon: '$company' },
                // COACH ROUTINES
                'company.routines': { name: vm.$t('titles.coach.routines'), icon: '$training', component: 'AddRoutine' },
                'company.routines.detail': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                'company.routines.edit': { name: vm.$t('titles.coach.routine.edit'), icon: '$training' },
                'company.routines.detail.assign': { name: vm.$t('titles.coach.routine.assign'), icon: '$training' },
                'company.routines.create': { name: vm.$t('titles.coach.routine.create'), icon: '$training' },
                // COACH PROGRAMS
                'company.routines.program.detail': { name: vm.$t('titles.coach.programs.detail'), icon: '$training' },
                'company.routines.program.detail.routine': { name: vm.$t('titles.coach.routine.detail'), icon: '$training' },
                'company.routines.program.assign': { name: vm.$t('titles.coach.programs.assign'), icon: '$training' },
                // COACH EXERCISES
                'company.exercises': { name: vm.$t('titles.coach.exercises'), icon: 'mdi-dumbbell' },
                // COMPANY CALENDAR
                'company.calendar': { name: vm.$t('titles.coach.calendar'), icon: '$calendar', component: 'Calendar' },
                'company.calendar.assignedRoutine': { name: vm.routineName, icon: '$calendar' },
                // COMPANY STATISTICS
                'company.statistics': { name: vm.$t('titles.coach.statistics'), icon: '$statistics' },
                // COMPANY PROFILE
                'company.profile': { name: vm.$t('titles.company.profile'), icon: '$account_circle', component: 'ConfigCompany' },
                'company.profile.edit': { name: vm.$t('titles.coach.profile_edit'), icon: '$account_circle' },
                'company.payment.edit': { name: vm.$t('titles.coach.payment_method_edit'), icon: '$account_circle' },
                // COMPANY COACH PROFILE
                'company.coaches.profile': { name: vm.$t('titles.company.coach_profile'), icon: '$company' },
                // COMPANY STUDENT PROFILE
                'company.students.profile': { name: vm.$t('titles.company.student_profile'), icon: '$company' },
            }
        },
        titleToolbar(vm = this) {
            return vm.routesInfo[vm.routeName]
        },
    },
    mounted () {
        this.onChangeColor()
    },
    methods: {
        ...mapMutations(['setShowCalendar']),
        goBack() {
            const vm = this
            vm.$router.go(-1)
        },
        hideCalendar() {
            const vm = this
            vm.setShowCalendar(false)
        },
        onChangeColor() {
            const vm = this;
            StatusBar.setStyle(vm.color === '#FFFFFF' ? 'light' : 'dark')
            StatusBar.setBackgroundColor(vm.color)
        }
    },
    watch: {
        color() {
            this.onChangeColor()
        }
    }
}
</script>

<style scoped>
.toolbar-title {
    font-weight: 700;
    font-size: 20px;
    line-height: 24px;
    color: #333333;
}
</style>